/**
 * KEY-MADE offline upload queue — Jobsite Capture Arc Stage 2A
 * (2026-06-15).
 *
 * Purpose: a durable queue of upload requests that survives reloads
 * (IndexedDB), drains in the background on Chromium via the service
 * worker's Background Sync API, and falls back to a page-driven drain
 * everywhere else (iOS Safari has no Background Sync but the IDB queue
 * still survives — items drain when the tab next becomes visible).
 *
 * Contract:
 *   - enqueue(spec) -> Promise<string> (the queued item id)
 *   - list() -> Promise<QueueItem[]>
 *   - remove(id) -> Promise<void>
 *   - retry(id) -> Promise<void> (resets status, kicks a drain)
 *   - drainNow() -> Promise<{processed, remaining}>
 *   - subscribe(listener) -> unsubscribe
 *   - reset() -> Promise<void> (test helper)
 *
 * Each QueueItem stores enough to rebuild the request from cold:
 *   { id, created_at, status, attempts, last_error?, last_attempt_at?,
 *     endpoint, method, headers, body_kind: 'json'|'multipart',
 *     body_data, parent_kind?, parent_id?, meta? }
 *
 * The schema mirrors public/sw.js — DO NOT change one without the other.
 */

const IDB_NAME = 'keymade-uploads';
const IDB_VERSION = 1;
const IDB_STORE = 'queue';
const MAX_ATTEMPTS = 5;
const MAX_QUEUE_SIZE = 200;     // hard cap — enqueue refuses past this
const STUCK_HIDDEN_THRESHOLD_MS = 60_000;
const SYNC_TAG = 'keymade-upload-queue';

let _listeners = new Set();
let _dbPromise = null;
let _drainInFlight = null;

function _openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        const store = db.createObjectStore(IDB_STORE, { keyPath: 'id' });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('created_at', 'created_at', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function _txStore(db, mode) {
  return db.transaction(IDB_STORE, mode).objectStore(IDB_STORE);
}

function _getAll(db) {
  return new Promise((resolve, reject) => {
    const store = _txStore(db, 'readonly');
    const req = store.getAll();
    req.onsuccess = () => {
      const arr = req.result || [];
      arr.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
      resolve(arr);
    };
    req.onerror = () => reject(req.error);
  });
}

function _put(db, item) {
  return new Promise((resolve, reject) => {
    const req = _txStore(db, 'readwrite').put(item);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function _delete(db, id) {
  return new Promise((resolve, reject) => {
    const req = _txStore(db, 'readwrite').delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function _count(db) {
  return new Promise((resolve, reject) => {
    const req = _txStore(db, 'readonly').count();
    req.onsuccess = () => resolve(req.result || 0);
    req.onerror = () => reject(req.error);
  });
}

async function _notify() {
  const items = await list();
  _listeners.forEach((l) => {
    try { l({ items }); } catch (_err) { /* swallow listener errors */ }
  });
}

function _newId() {
  return `q_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Enqueue an upload. `spec` shape:
 *   {
 *     endpoint: '/api/...',
 *     method?: 'POST' | 'PUT' | ...      (default POST)
 *     headers?: {Authorization: '...'}   (auth headers go here)
 *     bodyKind: 'json' | 'multipart',
 *     bodyData: object (for json) or {field: {kind:'file',blob,filename}|{kind:'value',value}} for multipart
 *     parentKind?: string  // e.g. 'site_visit_photo' — for UX labels
 *     parentId?: string
 *     meta?: object        // small breadcrumb (filename, project name, etc.)
 *   }
 * Returns the queued item id.
 */
export async function enqueue(spec) {
  if (!spec || !spec.endpoint) {
    throw new Error('enqueue requires spec.endpoint');
  }
  const db = await _openDb();
  const total = await _count(db);
  if (total >= MAX_QUEUE_SIZE) {
    const err = new Error(`Upload queue is full (${MAX_QUEUE_SIZE} items). Drain before adding more.`);
    err.code = 'QUEUE_FULL';
    throw err;
  }
  const item = {
    id: _newId(),
    created_at: new Date().toISOString(),
    status: 'pending',
    attempts: 0,
    endpoint: spec.endpoint,
    method: spec.method || 'POST',
    headers: spec.headers || {},
    body_kind: spec.bodyKind,
    body_data: spec.bodyData || {},
    parent_kind: spec.parentKind || null,
    parent_id: spec.parentId || null,
    meta: spec.meta || null,
  };
  await _put(db, item);
  await _notify();
  // Best-effort: register a background sync so the SW drains while the
  // tab is hidden. If unsupported, no-op — page-driven drain still works.
  await _registerBackgroundSync();
  // Kick a drain immediately if we're online + the page is visible.
  if (typeof navigator !== 'undefined' && navigator.onLine !== false) {
    // Don't await — enqueue returns fast; drain runs in the background.
    drainNow();
  }
  return item.id;
}

export async function list() {
  const db = await _openDb();
  return _getAll(db);
}

export async function remove(id) {
  const db = await _openDb();
  await _delete(db, id);
  await _notify();
}

export async function retry(id) {
  const db = await _openDb();
  const items = await _getAll(db);
  const item = items.find((i) => i.id === id);
  if (!item) return;
  await _put(db, {
    ...item,
    status: 'pending',
    last_error: null,
  });
  await _notify();
  drainNow();
}

export async function reset() {
  const db = await _openDb();
  const items = await _getAll(db);
  for (const it of items) {
    await _delete(db, it.id);
  }
  await _notify();
}

export function subscribe(listener) {
  _listeners.add(listener);
  // Fire once immediately so the subscriber doesn't sit blank until the
  // first mutation.
  list().then((items) => {
    try { listener({ items }); } catch (_err) { /* swallow */ }
  });
  return () => _listeners.delete(listener);
}

/**
 * Drain the queue once. Safe to call concurrently — repeated calls
 * share a single in-flight promise so we never double-fire a single
 * item.
 */
export function drainNow() {
  if (_drainInFlight) return _drainInFlight;
  _drainInFlight = (async () => {
    try {
      return await _drainImpl();
    } finally {
      _drainInFlight = null;
    }
  })();
  return _drainInFlight;
}

async function _drainImpl() {
  const db = await _openDb();
  const items = await _getAll(db);
  let processed = 0;
  for (const item of items) {
    if (item.status === 'uploaded') continue;
    if ((item.attempts || 0) >= MAX_ATTEMPTS) continue;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      // Bail early when explicitly offline — the queue will retry on the
      // next 'online' / visibility event.
      break;
    }
    try {
      await _put(db, { ...item, status: 'uploading', last_attempt_at: new Date().toISOString() });
      const req = _buildRequest(item);
      const res = await fetch(req);
      if (res.ok) {
        await _delete(db, item.id);
        processed += 1;
        let responseBody = null;
        try { responseBody = await res.clone().json(); } catch (_e) { /* not JSON */ }
        _listeners.forEach((l) => {
          try { l({ type: 'uploaded', id: item.id, response: responseBody, meta: item.meta }); } catch (_err) { /* swallow */ }
        });
      } else {
        const text = await res.text().catch(() => '');
        await _put(db, {
          ...item,
          status: 'failed',
          attempts: (item.attempts || 0) + 1,
          last_error: `HTTP ${res.status}: ${text.slice(0, 200)}`,
          last_attempt_at: new Date().toISOString(),
        });
      }
    } catch (err) {
      await _put(db, {
        ...item,
        status: 'failed',
        attempts: (item.attempts || 0) + 1,
        last_error: `Network: ${(err && err.message) || String(err)}`,
        last_attempt_at: new Date().toISOString(),
      });
    }
  }
  await _notify();
  return { processed, remaining: (await _getAll(db)).filter((i) => i.status !== 'uploaded').length };
}

function _buildRequest(item) {
  const init = {
    method: item.method || 'POST',
    headers: { ...(item.headers || {}) },
    credentials: 'omit',
  };
  if (item.body_kind === 'multipart') {
    const fd = new FormData();
    for (const [k, entry] of Object.entries(item.body_data || {})) {
      if (entry && entry.kind === 'file' && entry.blob) {
        fd.append(k, entry.blob, entry.filename || k);
      } else if (entry && entry.kind === 'value') {
        fd.append(k, String(entry.value));
      }
    }
    init.body = fd;
    delete init.headers['content-type'];
    delete init.headers['Content-Type'];
  } else if (item.body_kind === 'json') {
    init.body = JSON.stringify(item.body_data || {});
    if (!init.headers['content-type'] && !init.headers['Content-Type']) {
      init.headers['Content-Type'] = 'application/json';
    }
  }
  return new Request(item.endpoint, init);
}

async function _registerBackgroundSync() {
  if (typeof navigator === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    if (reg && reg.sync && typeof reg.sync.register === 'function') {
      await reg.sync.register(SYNC_TAG);
    }
  } catch (_err) {
    // Background Sync unsupported (iOS Safari) — page-driven drain
    // continues to handle this. No-op.
  }
}

// ---------------------------------------------------------------------------
// Stuck-while-hidden detection — used by UploadQueueIndicator to surface
// the "Uploads paused — reopen KEY-MADE" banner on iOS Safari.
// ---------------------------------------------------------------------------

export function describeStuckState({ items, isHidden, now = Date.now() }) {
  const pending = items.filter((i) => i.status !== 'uploaded' && (i.attempts || 0) < MAX_ATTEMPTS);
  if (!isHidden || pending.length === 0) return { stuck: false, pendingCount: pending.length };
  const oldest = pending.reduce((acc, it) => {
    const t = it.last_attempt_at || it.created_at;
    if (!t) return acc;
    const ts = new Date(t).getTime();
    return acc == null || ts < acc ? ts : acc;
  }, null);
  if (oldest == null) return { stuck: false, pendingCount: pending.length };
  return {
    stuck: now - oldest > STUCK_HIDDEN_THRESHOLD_MS,
    pendingCount: pending.length,
    stuckSinceMs: now - oldest,
  };
}

// ---------------------------------------------------------------------------
// Service-worker message bridge — when the SW drains in the background it
// posts the result here so subscribers can refresh their UI.
// ---------------------------------------------------------------------------

if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (e) => {
    const data = e.data || {};
    if (
      data.type === 'keymade-upload-done' ||
      data.type === 'keymade-upload-failed' ||
      data.type === 'keymade-upload-drain-complete'
    ) {
      // Re-fan the SW notification through our subscriber API so the
      // hook can react identically to page-driven and SW-driven drains.
      list().then((items) => {
        _listeners.forEach((l) => {
          try { l({ items, swEvent: data }); } catch (_err) { /* swallow */ }
        });
      });
    }
  });
}

export const __internal = {
  IDB_NAME, IDB_VERSION, IDB_STORE, MAX_ATTEMPTS, MAX_QUEUE_SIZE,
  STUCK_HIDDEN_THRESHOLD_MS, SYNC_TAG,
  _openDb,
};
